/** Nigerian VAT rate */
export const VAT_RATE = 0.075;

/** WHT rate for B2B services */
export const WHT_RATE = 0.05;

/** Currency */
export const CURRENCY = 'NGN';
export const CURRENCY_SYMBOL = '₦';

/** Naira to Kobo conversion */
export const KOBO_MULTIPLIER = 100;

/** Order number prefix */
export const ORDER_PREFIX = 'CY';

/** Time slots for pickup/delivery (2-hour windows) */
export const TIME_SLOTS = [
  { id: '09-11', label: '9:00 AM - 11:00 AM', startHour: 9, endHour: 11 },
  { id: '11-13', label: '11:00 AM - 1:00 PM', startHour: 11, endHour: 13 },
  { id: '13-15', label: '1:00 PM - 3:00 PM', startHour: 13, endHour: 15 },
  { id: '15-17', label: '3:00 PM - 5:00 PM', startHour: 15, endHour: 17 },
  { id: '17-19', label: '5:00 PM - 7:00 PM', startHour: 17, endHour: 19 },
  { id: '19-21', label: '7:00 PM - 9:00 PM', startHour: 19, endHour: 21 },
] as const;

/** Loyalty points config */
export const LOYALTY_POINTS_PER_NAIRA = 1 / 100; // 1 point per ₦100
export const LOYALTY_POINTS_VALUE = 1; // 1 point = ₦1 discount

/** Turnaround hours */
export const TURNAROUND_HOURS = {
  standard: 72,
  express: 48,
  express_max: 24,
  same_day: 8,
} as const;

/** Default express surcharge increment (Kobo) — ₦2,000 */
export const EXPRESS_SURCHARGE_INCREMENT_KOBO = 200_000;

/** Preset garment colors for POS item intake */
export const PRESET_COLORS = [
  { id: 'black', label: 'Black', hex: '#000000' },
  { id: 'white', label: 'White', hex: '#FFFFFF' },
  { id: 'navy', label: 'Navy Blue', hex: '#001F5C' },
  { id: 'royal_blue', label: 'Royal Blue', hex: '#2563EB' },
  { id: 'light_blue', label: 'Light Blue', hex: '#93C5FD' },
  { id: 'red', label: 'Red', hex: '#DC2626' },
  { id: 'burgundy', label: 'Burgundy', hex: '#7F1D1D' },
  { id: 'pink', label: 'Pink', hex: '#EC4899' },
  { id: 'green', label: 'Green', hex: '#16A34A' },
  { id: 'olive', label: 'Olive', hex: '#65A30D' },
  { id: 'yellow', label: 'Yellow', hex: '#EAB308' },
  { id: 'gold', label: 'Gold', hex: '#CA8A04' },
  { id: 'orange', label: 'Orange', hex: '#EA580C' },
  { id: 'brown', label: 'Brown', hex: '#78350F' },
  { id: 'beige', label: 'Beige', hex: '#D4B896' },
  { id: 'cream', label: 'Cream', hex: '#FFFDD0' },
  { id: 'grey', label: 'Grey', hex: '#6B7280' },
  { id: 'charcoal', label: 'Charcoal', hex: '#374151' },
  { id: 'purple', label: 'Purple', hex: '#7C3AED' },
  { id: 'maroon', label: 'Maroon', hex: '#7F1D1D' },
  { id: 'coral', label: 'Coral', hex: '#F97316' },
  { id: 'teal', label: 'Teal', hex: '#0D9488' },
  { id: 'multicolor', label: 'Multicolor', hex: '#GRADIENT' },
] as const;

/** Fabric damage types for POS item intake */
export const FABRIC_DAMAGE_TYPES = [
  { id: 'none', label: 'No Damage' },
  { id: 'stain', label: 'Stain' },
  { id: 'tear', label: 'Tear / Rip' },
  { id: 'hole', label: 'Hole' },
  { id: 'missing_button', label: 'Missing Button' },
  { id: 'broken_zipper', label: 'Broken Zipper' },
  { id: 'fading', label: 'Fading / Discolouration' },
  { id: 'shrinkage', label: 'Shrinkage' },
  { id: 'pilling', label: 'Pilling / Bobbling' },
  { id: 'burn_mark', label: 'Burn Mark' },
  { id: 'ink_mark', label: 'Ink Mark' },
  { id: 'mould', label: 'Mould / Mildew' },
  { id: 'loose_thread', label: 'Loose Thread' },
  { id: 'iron_mark', label: 'Iron Mark' },
  { id: 'color_bleed', label: 'Colour Bleed' },
  { id: 'worn_collar', label: 'Worn Collar' },
  { id: 'peeling_design', label: 'Peeling Design' },
  { id: 'soiled_collar', label: 'Soiled Collar' },
  { id: 'other', label: 'Other' },
] as const;

/**
 * Suggested clothing brands keyed by ItemCategory, prefilled with common brands
 * (especially generic western garments). These are only suggestions — the POS
 * brand field remains free-text so any custom/unlisted brand can be entered.
 */
export const BRAND_SUGGESTIONS: Record<string, string[]> = {
  shirts: ['Polo Ralph Lauren', 'Lacoste', 'Tommy Hilfiger', 'Calvin Klein', 'Zara', 'H&M', 'Uniqlo', 'Next', 'Marks & Spencer', 'Hugo Boss', 'Gucci', 'Fred Perry'],
  trousers: ["Levi's", 'Dockers', 'Tommy Hilfiger', 'Calvin Klein', 'Zara', 'H&M', 'Uniqlo', 'Next', 'Diesel', 'Gap', 'Wrangler'],
  suits: ['Hugo Boss', 'Armani', 'T.M. Lewin', 'Charles Tyrwhitt', 'Ted Baker', 'Zara', 'Next', 'Marks & Spencer', 'Moss Bros'],
  jackets: ['The North Face', 'Barbour', 'Superdry', 'Tommy Hilfiger', "Levi's", 'Zara', 'H&M', 'Uniqlo', 'Nike', 'Adidas'],
  coats: ['Burberry', 'The North Face', 'Superdry', 'Zara', 'Next', 'Marks & Spencer', 'Barbour'],
  dresses: ['Zara', 'H&M', 'Mango', 'ASOS', 'Ted Baker', 'Karen Millen', 'Next', 'River Island'],
  skirts: ['Zara', 'H&M', 'Mango', 'ASOS', 'Next', 'River Island'],
  delicates: ["Victoria's Secret", 'Marks & Spencer', 'Calvin Klein', 'La Perla'],
  uniforms: ['Nike', 'Adidas', 'Puma', 'Under Armour', 'Kappa'],
  traditional: [],
  bedding: [],
  towels: [],
  curtains: [],
  other: [],
};

/** Brands shown as suggestions regardless of garment category. */
export const COMMON_BRANDS = ['Zara', 'H&M', 'Nike', 'Adidas', 'Gucci', 'Polo Ralph Lauren', 'Tommy Hilfiger', 'Calvin Klein', 'Lacoste', 'Hugo Boss'];

/**
 * CleanYarn Price Catalog — Complete price list.
 * Organized by: Drycleaning, Laundry (Shirt, Native, Others), Beddings, Household.
 * Prices in Kobo (multiply Naira by 100).
 */
export interface CatalogItem {
  id: string;
  name: string;
  priceKobo: number;
  category: string; // maps to ItemCategory enum for API submissions
}

export const PRICE_CATALOG: Record<string, CatalogItem[]> = {
  drycleaning: [
    { id: 'dc-2pc-suit', name: '2 Piece Suit', priceKobo: 400_000, category: 'suits' },
    { id: 'dc-3pc-suit', name: '3 Piece Suit', priceKobo: 450_000, category: 'suits' },
    { id: 'dc-jacket', name: 'Jacket', priceKobo: 250_000, category: 'jackets' },
    { id: 'dc-blouse', name: 'Blouse', priceKobo: 200_000, category: 'delicates' },
    { id: 'dc-dress', name: 'Dress', priceKobo: 300_000, category: 'dresses' },
    { id: 'dc-dress-jacket', name: 'Dress & Jacket', priceKobo: 400_000, category: 'dresses' },
    { id: 'dc-jumpsuit', name: 'Jumpsuit', priceKobo: 400_000, category: 'dresses' },
    { id: 'dc-native-dryclean', name: 'Native Dryclean Only', priceKobo: 400_000, category: 'traditional' },
    { id: 'dc-native-agbada', name: 'Native Dryclean (Agbada)', priceKobo: 600_000, category: 'traditional' },
    { id: 'dc-scarf', name: 'Scarf', priceKobo: 150_000, category: 'other' },
    { id: 'dc-shawl', name: 'Shawl', priceKobo: 150_000, category: 'other' },
    { id: 'dc-shirt-short', name: 'Shirt & Short', priceKobo: 350_000, category: 'shirts' },
    { id: 'dc-shirt-trouser', name: 'Shirt & Trouser', priceKobo: 400_000, category: 'shirts' },
    { id: 'dc-trouser', name: 'Trouser', priceKobo: 200_000, category: 'trousers' },
    { id: 'dc-skirt', name: 'Skirt', priceKobo: 200_000, category: 'skirts' },
    { id: 'dc-tie', name: 'Tie', priceKobo: 150_000, category: 'other' },
    { id: 'dc-short', name: 'Short', priceKobo: 130_000, category: 'trousers' },
    { id: 'dc-wedding-dress', name: 'Wedding Dress', priceKobo: 2_500_000, category: 'dresses' },
    { id: 'dc-winter-coat-short', name: 'Winter Coat (Short)', priceKobo: 450_000, category: 'coats' },
    { id: 'dc-winter-coat-long', name: 'Winter Coat (Long)', priceKobo: 500_000, category: 'coats' },
    { id: 'dc-waist-coat', name: 'Waist Coat', priceKobo: 150_000, category: 'jackets' },
    { id: 'dc-head-gear', name: 'Head Gear', priceKobo: 200_000, category: 'traditional' },
    { id: 'dc-aso-oke-ipele', name: 'Aso-Oke (Ipele & Iborun)', priceKobo: 300_000, category: 'traditional' },
    { id: 'dc-aso-oke-complete', name: 'Aso-Oke Complete', priceKobo: 600_000, category: 'traditional' },
    { id: 'dc-camisole', name: 'Camisole', priceKobo: 150_000, category: 'delicates' },
  ],
  laundry_shirt: [
    { id: 'ls-shirt-hang', name: 'Shirt (Hang)', priceKobo: 100_000, category: 'shirts' },
    { id: 'ls-shirt-fold', name: 'Shirt (Fold)', priceKobo: 100_000, category: 'shirts' },
    { id: 'ls-shirt-trouser', name: 'Shirt & Trouser', priceKobo: 250_000, category: 'shirts' },
    { id: 'ls-shirt-short', name: 'Shirt & Short', priceKobo: 200_000, category: 'shirts' },
  ],
  laundry_native: [
    { id: 'ln-buba-sokoto-brocade', name: 'Buba & Sokoto (Brocade)', priceKobo: 250_000, category: 'traditional' },
    { id: 'ln-buba-sokoto-lace', name: 'Buba & Sokoto (Lace)', priceKobo: 300_000, category: 'traditional' },
    { id: 'ln-agbada-buba-sokoto-brocade', name: 'Agbada, Buba & Sokoto (Brocade)', priceKobo: 400_000, category: 'traditional' },
    { id: 'ln-agbada-buba-sokoto-lace', name: 'Agbada, Buba & Sokoto (Lace)', priceKobo: 450_000, category: 'traditional' },
    { id: 'ln-agbada-lace', name: 'Agbada Only (Lace)', priceKobo: 250_000, category: 'traditional' },
    { id: 'ln-boubou', name: 'Boubou', priceKobo: 250_000, category: 'traditional' },
    { id: 'ln-head-gear', name: 'Head Gear', priceKobo: 200_000, category: 'traditional' },
    { id: 'ln-igbo-blouse', name: 'Igbo Blouse', priceKobo: 200_000, category: 'traditional' },
    { id: 'ln-iro-buba-lace', name: 'Iro & Buba (Lace)', priceKobo: 300_000, category: 'traditional' },
    { id: 'ln-iro-buba-brocade', name: 'Iro & Buba (Brocade)', priceKobo: 250_000, category: 'traditional' },
    { id: 'ln-jalamia', name: 'Jalamia', priceKobo: 200_000, category: 'traditional' },
  ],
  laundry_others: [
    { id: 'lo-boxers', name: 'Boxers', priceKobo: 60_000, category: 'other' },
    { id: 'lo-tshirt', name: 'T-Shirt', priceKobo: 80_000, category: 'shirts' },
    { id: 'lo-vest', name: 'Vest', priceKobo: 80_000, category: 'shirts' },
    { id: 'lo-cardigan', name: 'Cardigan', priceKobo: 120_000, category: 'jackets' },
    { id: 'lo-socks', name: 'Socks', priceKobo: 54_000, category: 'other' },
    { id: 'lo-sweatshirt', name: 'Sweatshirt', priceKobo: 90_000, category: 'shirts' },
    { id: 'lo-top-trouser', name: 'Top & Trouser', priceKobo: 250_000, category: 'shirts' },
    { id: 'lo-top-short', name: 'Top & Short', priceKobo: 200_000, category: 'shirts' },
    { id: 'lo-track-suit', name: 'Track Suit', priceKobo: 300_000, category: 'uniforms' },
    { id: 'lo-track-top', name: 'Track Top', priceKobo: 200_000, category: 'shirts' },
    { id: 'lo-track-bottom', name: 'Track Bottom', priceKobo: 200_000, category: 'trousers' },
  ],
  beddings: [
    { id: 'b-bathrobe', name: 'Bathrobe', priceKobo: 200_000, category: 'towels' },
    { id: 'b-bed-sheet-double', name: 'Bed Sheet Double', priceKobo: 250_000, category: 'bedding' },
    { id: 'b-bed-sheet-single', name: 'Bed Sheet Single', priceKobo: 200_000, category: 'bedding' },
    { id: 'b-blanket', name: 'Blanket', priceKobo: 300_000, category: 'bedding' },
    { id: 'b-duvet', name: 'Duvet', priceKobo: 400_000, category: 'bedding' },
    { id: 'b-hand-towel', name: 'Hand Towel', priceKobo: 100_000, category: 'towels' },
    { id: 'b-face-towel', name: 'Face Towel', priceKobo: 80_000, category: 'towels' },
    { id: 'b-medium-towel', name: 'Medium Towel', priceKobo: 200_000, category: 'towels' },
    { id: 'b-large-towel', name: 'Large Towel', priceKobo: 300_000, category: 'towels' },
    { id: 'b-pillowcase', name: 'Pillowcase', priceKobo: 500_000, category: 'bedding' },
  ],
  household: [
    { id: 'h-light-curtains', name: 'Light Curtains (per yard)', priceKobo: 150_000, category: 'curtains' },
    { id: 'h-heavy-curtains', name: 'Heavy Curtains (per yard)', priceKobo: 200_000, category: 'curtains' },
    { id: 'h-rugs', name: 'Rugs (From)', priceKobo: 1_500_000, category: 'other' },
    { id: 'h-teddy-bear', name: 'Teddy Bear (From)', priceKobo: 1_000_000, category: 'other' },
  ],
};

/** Business bank details for invoice/payment */
/**
 * @deprecated Bank details now live in the `bank_accounts` table and are served
 * by GET /v1/bank-transfer/accounts. Read them from there — a bundled constant
 * cannot be corrected without shipping a new build of the mobile app, and a
 * stale account number sends a customer's money somewhere it cannot be recovered
 * from. Migration 0012 seeded this exact row as `bank_wema_primary`.
 *
 * Kept only so an external consumer importing it does not break at the type
 * level; nothing in this repo reads it.
 */
export const BANK_DETAILS = {
  accountName: 'Clean Yarn Ltd',
  bankName: 'Wema Bank',
  accountNumber: '0125512411',
} as const;

/** GPS tracking interval for driver app (ms) */
export const DRIVER_GPS_INTERVAL_MS = 30_000; // 30 seconds

/** Session config */
export const SESSION_CONFIG = {
  accessTokenExpiryMinutes: 60,
  refreshTokenExpiryDays: 30,
  customerSessionDays: 30,
  adminSessionHours: 24,
  driverSessionHours: 12,
  maxConcurrentSessions: 3,
} as const;

/** Rate limits (requests per minute) */
export const RATE_LIMITS = {
  public: 30,
  authenticated: 120,
  login: 5,
  finoraApi: 300,
} as const;

/** Max file upload size (bytes) */
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

/** Nigerian phone regex */
export const NIGERIAN_PHONE_REGEX = /^\+234[0-9]{10}$/;

/** Password requirements (PRD FR-1.1) */
export const PASSWORD_MIN_LENGTH = 12;
